import { useEmbedding } from "../Embedding";
import React from "react";

import * as TipTap from "@tiptap/react";

import { isUploadInProgress } from "./Blob";
import { createRichEditorExtensions } from "./createRichEditorExtensions";
import { clearLocalDraft, isRichTextEmpty, LocalDraftOptions, readLocalDraft, writeLocalDraft } from "./localDrafts";
import { SearchFn } from "./extensions/MentionPeople";
import { restoreRichTextSource } from "../RichContent/restoreSource";
import { createResourceLinkPlugin, type ResolveResourceLinks } from "./extensions/Link/resourceLinks";
import { normalizeRichTextContent } from "./richTextContent";

export interface Person {
  id: string;
  fullName: string;
  avatarUrl: string | null;
  title: string;
  profileLink: string;
}

interface OnSaveData {
  json: any;
  html: string;
}

interface OnBlurData {
  json: any;
  html: string;
}

export type UploadFileFn = (file: File, onProgress: (progress: number) => void) => Promise<{ id: string; url: string }>;
export type MentionedPersonLookupFn = (id: string) => Promise<Person | null>;

export interface RichEditorHandlers {
  resolveResourceLinks: ResolveResourceLinks | null;
  mentionedPersonLookup: MentionedPersonLookupFn;
  peopleSearch?: SearchFn;
  uploadFile?: UploadFileFn;
}

interface UseEditorProps {
  placeholder?: string;
  content?: any;
  onSave?: (data: OnSaveData) => void;
  onBlur?: (data: OnBlurData) => void;
  onUpdate?: (data: { json: any; html: string }) => void;
  onUploadStatusChange?: (uploading: boolean) => void;
  className?: string;
  editable?: boolean;
  autoFocus?: boolean;
  tabindex?: string;
  localDraft?: LocalDraftOptions;
  thumbnailBlobs?: boolean;
  transformContent?: (content: any) => any;

  handlers: RichEditorHandlers;
}

export interface EditorState {
  editor: any;
  submittable: boolean;
  focused: boolean;
  empty: boolean;
  uploading: boolean;
  linkEditActive: boolean;
  setLinkEditActive: (active: boolean) => void;
  mentionedPersonLookup?: MentionedPersonLookupFn;
  uploadFile?: UploadFileFn;
  setContent: (content: any) => void;
  setFocused: (focused: boolean) => void;
  getJson: () => any;
  localDraftRestored: boolean;
  clearLocalDraft: () => void;
}

const DEFAULT_EDITOR_PROPS: Partial<UseEditorProps> = {
  editable: true,
  autoFocus: false,
  tabindex: "",
};

export function useEditor(props: UseEditorProps): EditorState {
  props = { ...DEFAULT_EDITOR_PROPS, ...props };
  const embedding = useEmbedding();

  const [linkEditActive, setLinkEditActive] = React.useState(false);
  const [submittable, setSubmittable] = React.useState(true);
  const [focused, setFocused] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [baseContent] = React.useState(() =>
    normalizeRichTextContent(props.editable ? restoreRichTextSource(props.content) : props.content),
  );
  const [restoredDraft] = React.useState(() => readLocalDraft(props.localDraft, baseContent));
  const sourceContent = props.editable ? (restoredDraft ?? baseContent) : baseContent;
  const initialContent = normalizeRichTextContent(props.transformContent?.(sourceContent) ?? sourceContent);
  const [empty, setEmpty] = React.useState(isRichTextEmpty(initialContent));

  const extensions = React.useMemo(
    () =>
      createRichEditorExtensions(props.handlers, {
        editable: props.editable,
        placeholder: props.placeholder,
        thumbnailBlobs: props.thumbnailBlobs,
        portalContainer: embedding?.portalContainer,
      }),
    [
      props.handlers.uploadFile,
      props.handlers.peopleSearch,
      props.editable,
      props.placeholder,
      props.thumbnailBlobs,
      embedding?.portalContainer,
    ],
  );

  const editorProps = React.useMemo(
    () => ({
      attributes: {
        class: "focus:outline-none" + " " + props.className,
        tabindex: props.tabindex!,
      },
    }),
    [props.className, props.tabindex],
  );

  const editor = TipTap.useEditor({
    shouldRerenderOnTransaction: true,
    // Markdown shortcuts apply only while typing, except Highlight's
    // "==text==" paste behavior. Link handles URLs during clipboard parsing.
    enablePasteRules: ["highlight"],
    editable: props.editable,
    content: initialContent,
    autofocus: props.autoFocus,
    injectCSS: false,
    editorProps,
    extensions,
    onFocus({ editor }) {
      editor.chain().unsetFakeTextSelection().run();

      setFocused(true);
    },
    onBlur: ({ editor }) => {
      setFocused(false);

      if (!props.onBlur) return;

      props.onBlur({
        json: props.editable ? restoreRichTextSource(editor.getJSON()) : editor.getJSON(),
        html: editor.getHTML(),
      });
    },
    onUpdate: ({ editor }) => {
      const isUploading = isUploadInProgress(editor.state.doc);

      if (props.onUploadStatusChange && isUploading !== uploading) {
        props.onUploadStatusChange(isUploading);
      }

      setUploading(isUploading);
      setSubmittable(!isUploading);

      const json = props.editable ? restoreRichTextSource(editor.getJSON()) : editor.getJSON();

      setEmpty(editor.state.doc.childCount === 1 && editor.state.doc.firstChild?.childCount === 0);

      if (props.editable && !isUploading) {
        writeLocalDraft(props.localDraft, json, baseContent);
      }

      if (props.onUpdate) {
        props.onUpdate({
          json,
          html: editor.getHTML(),
        });
      }
    },
  });

  React.useEffect(() => {
    if (!editor || !props.editable || !props.handlers.resolveResourceLinks) return;

    const plugin = createResourceLinkPlugin(props.handlers.resolveResourceLinks);
    editor.registerPlugin(plugin);

    return () => {
      editor.unregisterPlugin("resourceLinkTitles");
    };
  }, [editor, props.editable, props.handlers.resolveResourceLinks]);

  const setContent = React.useCallback(
    (content: any) => {
      if (!editor) return;
      const source = props.editable ? restoreRichTextSource(content) : content;
      editor.commands.setContent(normalizeRichTextContent(props.transformContent?.(source) ?? source), {
        emitUpdate: false,
      });
    },
    [editor, props.transformContent, props.editable],
  );

  // Synchronize read-only readers when their backend response changes.
  const contentKey = props.editable ? "" : JSON.stringify(props.content ?? null);
  React.useEffect(() => {
    if (!editor || props.editable) return;
    setContent(JSON.parse(contentKey));
  }, [editor, props.editable, contentKey, setContent]);

  const getJson = React.useCallback(() => {
    if (!editor) return null;
    return props.editable ? restoreRichTextSource(editor.getJSON()) : editor.getJSON();
  }, [editor, props.editable]);

  const clearDraft = React.useCallback(() => {
    clearLocalDraft(props.localDraft);
  }, [props.localDraft?.key, props.localDraft?.enabled, props.localDraft?.ttlMs]);

  React.useEffect(() => {
    if (!editor) return;

    if (focused) {
      editor.chain().focus().run();
    }
  }, [editor, focused]);

  return {
    editor,
    submittable,
    focused,
    empty,
    uploading,
    linkEditActive,
    setLinkEditActive,
    mentionedPersonLookup: props.handlers.mentionedPersonLookup,
    uploadFile: props.handlers.uploadFile,
    setContent,
    setFocused,
    getJson,
    localDraftRestored: Boolean(restoredDraft),
    clearLocalDraft: clearDraft,
  };
}
